import { useRef, useState, type FormEvent } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ConfigOutput } from "@cx/contracts";
import { ApiFailure, apiFetch } from "../api";

export function LoginPage() {
  const qc = useQueryClient();
  // A site key por ambiente vem do Worker (decisão 9): o build do SPA é o mesmo em staging e produção.
  const config = useQuery({
    queryKey: ["config"],
    queryFn: () => apiFetch<ConfigOutput>("/config"),
    staleTime: Infinity,
    retry: false,
  });
  const ts = useRef<TurnstileInstance>(null);
  const [step, setStep] = useState<"password" | "mfa">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // O token do Turnstile é de uso único: depois de uma tentativa recusada, o widget gera outro (FE10).
  const dropToken = () => {
    setToken(null);
    ts.current?.reset();
  };

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      if (step === "password") {
        const r = await apiFetch<{ ok?: true; mfaRequired?: true }>("/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password, turnstileToken: token }),
        });
        if (r.mfaRequired) {
          setStep("mfa");
          return;
        }
      } else {
        await apiFetch("/auth/mfa", { method: "POST", body: JSON.stringify({ code }) });
      }
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : "Não foi possível entrar. Tente de novo.");
      if (step === "password") dropToken();
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-vh-100 d-flex align-items-center justify-content-center bg-body-tertiary p-3">
      <form
        className="card shadow-sm p-4"
        style={{ width: "100%", maxWidth: 400 }}
        onSubmit={submit}
        noValidate
      >
        <h1 className="h4 mb-4 text-center">Entrar no CX ERP</h1>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {step === "password" ? (
          <>
            <label className="form-label" htmlFor="email">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              className="form-control mb-3"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <label className="form-label" htmlFor="password">
              Senha
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="form-control mb-3"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {config.data ? (
              <Turnstile
                ref={ts}
                siteKey={config.data.turnstileSiteKey}
                onSuccess={setToken}
                onExpire={() => setToken(null)}
                onError={() => setToken(null)}
                options={{ action: config.data.turnstileAction, language: "pt-BR" }}
                className="mb-3"
              />
            ) : config.isError ? (
              <div className="alert alert-warning d-flex align-items-center gap-2">
                <span>Não foi possível carregar a verificação de segurança.</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary ms-auto"
                  onClick={() => config.refetch()}
                >
                  Tentar de novo
                </button>
              </div>
            ) : (
              <p className="text-muted small" role="status">
                Carregando verificação…
              </p>
            )}
          </>
        ) : (
          <>
            <label className="form-label" htmlFor="code">
              Código do autenticador
            </label>
            <input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="form-control mb-3"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              autoFocus
            />
          </>
        )}
        <button
          type="submit"
          className="btn btn-primary w-100"
          disabled={busy || (step === "password" && !token)}
        >
          {busy ? "Entrando…" : "Entrar"}
        </button>
      </form>
    </main>
  );
}
